// src/ClientsPanel.js
import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from './supabaseClient';
import SuiviInstallations from './SuiviInstallations';
import { useAuth } from './AuthContext';
import { useNavigate } from 'react-router-dom';
import { useQuote } from './QuoteContext';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Plus, Search, Edit2, Trash2, User, ChevronDown, AlertCircle } from 'lucide-react';

const STATUTS = {
  prospect: { label: 'Prospect',  color: '#f59e0b', bg: '#fef3c7' },
  client:   { label: 'Client',    color: '#10b981', bg: '#d1fae5' },
  inactif:  { label: 'Inactif',   color: '#6b7280', bg: '#f3f4f6' },
};

const STATUT_DEVIS = {
  en_cours: { label: 'En cours', color: '#f59e0b' },
  gagne:    { label: 'Gagné',    color: '#10b981' },
  perdu:    { label: 'Perdu',    color: '#ef4444' },
  archive:  { label: 'Archivé',  color: '#9ca3af' },
};

const STATUT_FACTURE = {
  generee:    { label: 'Générée',    color: '#6b7280' },
  emise:      { label: 'Émise',      color: '#f59e0b' },
  en_attente: { label: 'En attente', color: '#3b82f6' },
  payee:      { label: 'Payée',      color: '#10b981' },
  annulee:    { label: 'Annulée',    color: '#ef4444' },
};

const fmt = (n) => new Intl.NumberFormat('fr-FR').format(Math.round(n || 0));
const fmtDate = (d) => {
  if (!d) return '—';
  const parts = d.split('-');
  if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
  return d;
};

const emptyForm = {
  nom: '', email: '', mobile: '', telephone: '',
  ville: '', pays: 'Sénégal', produit_interet_id: '',
  statut: 'prospect', notes: '',
};

export default function ClientsPanel({ isManager = false }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { resetQuoteData, updateQuoteData } = useQuote();
  const [clients, setClients] = useState([]);
  const [produits, setProduits] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatut, setFilterStatut] = useState('tous');
  const [filterProduit, setFilterProduit] = useState('tous');
  const [showAll, setShowAll] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editClient, setEditClient] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [showProduitForm, setShowProduitForm] = useState(false);
  const [newProduit, setNewProduit] = useState('');
  const [savingProduit, setSavingProduit] = useState(false);
  const [expandedClient, setExpandedClient] = useState(null);
  const [clientHistory, setClientHistory] = useState({});
  const [loadingHistory, setLoadingHistory] = useState(null);

  // ── Chargement ────────────────────────────────────────────────────────────
  const fetchAll = useCallback(async () => {
    setLoading(true);
    const [{ data: c }, { data: p }, { data: pr }] = await Promise.all([
      supabase.from('clients')
        .select('*, produits_interet(nom), profiles(nom)')
        .order('created_at', { ascending: false }),
      supabase.from('produits_interet').select('*').eq('actif', true).order('ordre'),
      isManager ? supabase.from('profiles').select('id, nom').eq('role', 'commerciale') : { data: [] },
    ]);
    setClients(c || []);
    setProduits(p || []);
    setProfiles(pr || []);
    setLoading(false);
  }, [isManager]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ── Historique 360° ───────────────────────────────────────────────────────
  const fetchClientHistory = async (client) => {
    if (clientHistory[client.id]) return;
    if (!client.email) {
      setClientHistory(prev => ({ ...prev, [client.id]: { quotes: [], invoices: [], reminders: [] } }));
      return;
    }
    setLoadingHistory(client.id);

    // Charger tous les devis et filtrer par email
    const { data: quotesData } = await supabase
      .from('quotes')
      .select('id, quote_number, date, statut, total_with_vat, client_info')
      .order('created_at', { ascending: false });

    const clientQuotes = (quotesData || []).filter(q => {
      const qEmail = (q.client_info?.email || q.client_info?.clientEmail || '').toLowerCase().trim();
      return qEmail === client.email.toLowerCase().trim();
    });

    let invoices = [];
    let reminders = [];

    if (clientQuotes.length > 0) {
      const quoteIds = clientQuotes.map(q => q.id);
      const [{ data: invData }, { data: remData }] = await Promise.all([
        supabase.from('invoices')
          .select('id, invoice_number, type, montant, statut, date_emission, date_paiement, echeance_numero, echeance_total, quote_id')
          .in('quote_id', quoteIds)
          .order('created_at', { ascending: false }),
        supabase.from('quote_reminders')
          .select('id, quote_id, remind_at, message, statut, created_at')
          .in('quote_id', quoteIds)
          .order('remind_at', { ascending: false })
          .limit(10),
      ]);
      invoices = invData || [];
      reminders = remData || [];
    }

    setClientHistory(prev => ({ ...prev, [client.id]: { quotes: clientQuotes, invoices, reminders } }));
    setLoadingHistory(null);
  };

  const handleToggleExpand = (client) => {
    const newExpanded = expandedClient === client.id ? null : client.id;
    setExpandedClient(newExpanded);
    if (newExpanded) fetchClientHistory(client);
  };

  // ── Filtrage ──────────────────────────────────────────────────────────────
  const filtered = clients.filter(c => {
    const matchSearch = !search ||
      c.nom?.toLowerCase().includes(search.toLowerCase()) ||
      c.email?.toLowerCase().includes(search.toLowerCase()) ||
      c.mobile?.includes(search) ||
      c.ville?.toLowerCase().includes(search.toLowerCase());
    const matchStatut = filterStatut === 'tous' || c.statut === filterStatut;
    const matchProduit = filterProduit === 'tous' || c.produit_interet_id === filterProduit;
    return matchSearch && matchStatut && matchProduit;
  });

  const displayed = showAll ? filtered : filtered.slice(0, 5);

  // ── Sauvegarde ────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!form.nom.trim()) return;
    setSaving(true);
    const payload = {
      ...form,
      produit_interet_id: form.produit_interet_id || null,
      commerciale_id: editClient?.commerciale_id || user.id,
      updated_at: new Date().toISOString(),
    };
    if (editClient) {
      await supabase.from('clients').update(payload).eq('id', editClient.id);
    } else {
      await supabase.from('clients').insert({ ...payload, source: 'manuel' });
    }
    setSaving(false);
    setShowForm(false);
    setEditClient(null);
    setForm(emptyForm);
    fetchAll();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce client ?')) return;
    await supabase.from('clients').delete().eq('id', id);
    fetchAll();
  };

  const handleAddProduit = async () => {
    if (!newProduit.trim()) return;
    setSavingProduit(true);
    await supabase.from('produits_interet').insert({ nom: newProduit.trim(), ordre: produits.length + 1 });
    setNewProduit('');
    setShowProduitForm(false);
    setSavingProduit(false);
    fetchAll();
  };

  const nouveauDevisClient = (client) => {
    resetQuoteData();
    updateQuoteData({ clientInfo: {
      clientName: client.nom || '',
      attentionName: client.nom || '',
      attentionTitle: 'M.',
      email: client.email || '',
      mobile: client.mobile || '',
      phone: client.telephone || '',
      address: client.ville || '',
      region: client.pays || 'Sénégal',
    }});
    localStorage.removeItem('quoteFormData');
    window.open('/quote-form', '_blank');
  };

  const openEdit = (c) => {
    setEditClient(c);
    setForm({
      nom: c.nom || '', email: c.email || '', mobile: c.mobile || '',
      telephone: c.telephone || '', ville: c.ville || '', pays: c.pays || 'Sénégal',
      produit_interet_id: c.produit_interet_id || '',
      statut: c.statut || 'prospect', notes: c.notes || '',
    });
    setShowForm(true);
  };

  const inputStyle = {
    width: '100%', padding: '8px 12px', border: '1px solid #e5e7eb',
    borderRadius: '8px', fontSize: '13px', outline: 'none', boxSizing: 'border-box',
  };
  const labelStyle = { fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '4px', display: 'block' };

  // ── Composant Historique 360° ─────────────────────────────────────────────
  const HistoriqueClient = ({ client }) => {
    const h = clientHistory[client.id];

    if (loadingHistory === client.id) {
      return (
        <div style={{ padding: '16px', textAlign: 'center', color: '#9ca3af', fontSize: '12px',
          borderTop: '1px solid #f3f4f6', background: '#fafbff' }}>
          ⏳ Chargement de l'historique...
        </div>
      );
    }
    if (!h) return null;

    const { quotes, invoices, reminders } = h;

    // Synthèse
    const totalGagne = quotes.filter(q => q.statut === 'gagne').length;
    const montantGagne = quotes.filter(q => q.statut === 'gagne').reduce((s, q) => s + (q.total_with_vat || 0), 0);
    const montantEncaisse = invoices.filter(i => i.statut === 'payee').reduce((s, i) => s + (i.montant || 0), 0);
    const facturesEnAttente = invoices.filter(i => ['emise', 'en_attente'].includes(i.statut));
    const derniereRelance = reminders.find(r => r.statut === 'done');
    const prochaineRelance = reminders.find(r => r.statut === 'pending');

    return (
      <div style={{ borderTop: '1px solid #e5e7eb', background: '#fafbff' }}>
        {/* Header historique */}
        <div style={{ padding: '10px 16px', background: '#eff6ff', borderBottom: '1px solid #dbeafe',
          fontSize: '12px', fontWeight: 700, color: '#1e40af', display: 'flex', alignItems: 'center', gap: '6px' }}>
          📊 Historique client 360°
          {!client.email && <span style={{ color: '#ef4444', fontWeight: 400 }}>— Email manquant, impossible de charger l'historique</span>}
        </div>

        <div style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>

          {/* Synthèse financière */}
          {quotes.length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' }}>
              {[
                { label: 'Devis émis',   value: quotes.length,                    color: '#3b82f6', icon: '📄' },
                { label: 'Devis gagnés', value: totalGagne,                       color: '#10b981', icon: '✅' },
                { label: 'CA gagné',     value: fmt(montantGagne) + ' FCFA',      color: '#8b5cf6', icon: '💰' },
                { label: 'Encaissé',     value: fmt(montantEncaisse) + ' FCFA',   color: '#10b981', icon: '💳' },
              ].map((s, i) => (
                <div key={i} style={{ background: 'white', borderRadius: '8px', padding: '10px',
                  border: '1px solid #e5e7eb', textAlign: 'center' }}>
                  <div style={{ fontSize: '16px', marginBottom: '4px' }}>{s.icon}</div>
                  <div style={{ fontSize: '12px', fontWeight: 700, color: s.color }}>{s.value}</div>
                  <div style={{ fontSize: '10px', color: '#9ca3af', marginTop: '2px' }}>{s.label}</div>
                </div>
              ))}
            </div>
          )}

          {/* Alerte factures en attente */}
          {facturesEnAttente.length > 0 && (
            <div style={{ background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: '8px',
              padding: '8px 12px', fontSize: '12px', color: '#92400e',
              display: 'flex', alignItems: 'center', gap: '6px' }}>
              <AlertCircle size={13} />
              <strong>{facturesEnAttente.length} facture(s) en attente</strong> —
              {fmt(facturesEnAttente.reduce((s, i) => s + (i.montant || 0), 0))} FCFA non encaissés
            </div>
          )}

          {/* Relances */}
          {(derniereRelance || prochaineRelance) && (
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {derniereRelance && (
                <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '6px',
                  padding: '6px 10px', fontSize: '11px', color: '#065f46', flex: 1, minWidth: '180px' }}>
                  ✅ <strong>Dernière relance :</strong> {fmtDate(derniereRelance.remind_at)}<br/>
                  <span style={{ color: '#6b7280' }}>{derniereRelance.message}</span>
                </div>
              )}
              {prochaineRelance && (
                <div style={{ background: '#fefce8', border: '1px solid #fcd34d', borderRadius: '6px',
                  padding: '6px 10px', fontSize: '11px', color: '#92400e', flex: 1, minWidth: '180px' }}>
                  🔔 <strong>Prochaine relance :</strong> {fmtDate(prochaineRelance.remind_at)}<br/>
                  <span style={{ color: '#6b7280' }}>{prochaineRelance.message}</span>
                </div>
              )}
            </div>
          )}

          {/* Devis + Factures */}
          {quotes.length === 0 ? (
            <div style={{ fontSize: '12px', color: '#9ca3af', fontStyle: 'italic', textAlign: 'center', padding: '8px' }}>
              Aucun devis trouvé pour cet email client
            </div>
          ) : (
            <div>
              <div style={{ fontSize: '11px', fontWeight: 700, color: '#374151',
                textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>
                📄 Devis & Factures ({quotes.length})
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {quotes.map(q => {
                  const ds = STATUT_DEVIS[q.statut] || STATUT_DEVIS.en_cours;
                  const qInvoices = invoices.filter(i => i.quote_id === q.id);
                  return (
                    <div key={q.id} style={{ background: 'white', border: '1px solid #e5e7eb',
                      borderRadius: '8px', padding: '10px 12px' }}>
                      {/* En-tête devis */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        marginBottom: qInvoices.length > 0 ? '8px' : '0' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <span style={{ fontWeight: 700, fontSize: '13px', color: '#1e40af' }}>{q.quote_number}</span>
                          <span style={{ fontSize: '11px', color: '#9ca3af' }}>{fmtDate(q.date)}</span>
                          <span style={{ fontSize: '11px', color: ds.color, fontWeight: 600,
                            background: ds.color + '18', padding: '2px 8px', borderRadius: '999px' }}>
                            {ds.label}
                          </span>
                        </div>
                        <span style={{ fontSize: '12px', fontWeight: 700, color: '#374151' }}>
                          {fmt(q.total_with_vat)} FCFA
                        </span>
                      </div>
                      {/* Factures liées */}
                      {qInvoices.length > 0 && (
                        <div style={{ paddingTop: '6px', borderTop: '1px dashed #f0f0f0',
                          display: 'flex', flexDirection: 'column', gap: '3px' }}>
                          {qInvoices.map(inv => {
                            const fs = STATUT_FACTURE[inv.statut] || STATUT_FACTURE.generee;
                            const typeLabel = inv.type === 'echeance'
                              ? `Éch. ${inv.echeance_numero - 1}/${inv.echeance_total - 1}`
                              : inv.type === 'acompte' ? 'Acompte' : 'Solde';
                            return (
                              <div key={inv.id} style={{ display: 'flex', justifyContent: 'space-between',
                                alignItems: 'center', fontSize: '11px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#6b7280' }}>
                                  <span>🧾</span>
                                  <span style={{ color: '#374151' }}>{inv.invoice_number}</span>
                                  <span style={{ color: '#9ca3af' }}>{typeLabel}</span>
                                  {inv.date_emission && <span style={{ color: '#d1d5db' }}>émise le {fmtDate(inv.date_emission)}</span>}
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <span style={{ fontWeight: 600, color: '#374151' }}>{fmt(inv.montant)} FCFA</span>
                                  <span style={{ color: fs.color, fontWeight: 600, fontSize: '10px',
                                    background: fs.color + '18', padding: '2px 6px', borderRadius: '4px' }}>
                                    {fs.label}
                                  </span>
                                  {inv.date_paiement && (
                                    <span style={{ color: '#9ca3af' }}>payée le {fmtDate(inv.date_paiement)}</span>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div style={{ padding: '0' }}>
      {/* ── Barre outils ── */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: '200px' }}>
          <Search size={14} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
          <input value={search} onChange={e => { setSearch(e.target.value); setShowAll(false); }}
            placeholder="Rechercher un client..."
            style={{ ...inputStyle, paddingLeft: '32px' }} />
        </div>
        <select value={filterStatut} onChange={e => { setFilterStatut(e.target.value); setShowAll(false); }}
          style={{ ...inputStyle, width: 'auto', cursor: 'pointer' }}>
          <option value="tous">Tous statuts</option>
          {Object.entries(STATUTS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <select value={filterProduit} onChange={e => { setFilterProduit(e.target.value); setShowAll(false); }}
          style={{ ...inputStyle, width: 'auto', cursor: 'pointer' }}>
          <option value="tous">Tous produits</option>
          {produits.map(p => <option key={p.id} value={p.id}>{p.nom}</option>)}
        </select>
        {isManager && (
          <button onClick={() => setShowProduitForm(true)}
            style={{ padding: '8px 14px', background: '#f3f4f6', border: '1px solid #e5e7eb',
              borderRadius: '8px', fontSize: '13px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
            + Produit
          </button>
        )}
        <button onClick={() => { setEditClient(null); setForm(emptyForm); setShowForm(true); }}
          style={{ padding: '8px 16px', background: '#1e40af', color: 'white', border: 'none',
            borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
          + Nouveau client
        </button>
      </div>

      {/* ── Compteur ── */}
      <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '12px' }}>
        {filtered.length} client{filtered.length > 1 ? 's' : ''}
        {clients.length !== filtered.length ? ` sur ${clients.length}` : ''}
        {!showAll && filtered.length > 5 && (
          <span style={{ color: '#9ca3af' }}> — 5 affichés</span>
        )}
      </div>

      {/* ── Liste clients ── */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#9ca3af' }}>Chargement...</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#9ca3af' }}>
          {search || filterStatut !== 'tous' ? 'Aucun résultat' : 'Aucun client pour le moment'}
        </div>
      ) : (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {displayed.map(c => {
              const statut = STATUTS[c.statut] || STATUTS.prospect;
              const isExpanded = expandedClient === c.id;
              const h = clientHistory[c.id];
              const hasAlerts = h?.invoices?.filter(i => ['emise', 'en_attente'].includes(i.statut)).length > 0;
              const hasPendingReminder = h?.reminders?.find(r => r.statut === 'pending');

              return (
                <motion.div key={c.id}
                  initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
                  style={{ background: 'white',
                    border: `1px solid ${hasAlerts ? '#fcd34d' : '#e5e7eb'}`,
                    borderRadius: '10px', overflow: 'hidden',
                    boxShadow: hasAlerts ? '0 1px 4px rgba(251,191,36,0.2)' : '0 1px 4px rgba(0,0,0,0.05)' }}>

                  {/* Ligne principale */}
                  <div style={{ display: 'flex', alignItems: 'center', padding: '12px 16px', gap: '12px', cursor: 'pointer' }}
                    onClick={() => handleToggleExpand(c)}>
                    <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: '#eff6ff',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <User size={16} color="#3b82f6" />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: '14px', color: '#111827', marginBottom: '2px' }}>{c.nom}</div>
                      <div style={{ fontSize: '12px', color: '#6b7280', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                        {c.email && <span>📧 {c.email}</span>}
                        {c.mobile && <span>📱 {c.mobile}</span>}
                        {c.ville && <span>📍 {c.ville}</span>}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexShrink: 0 }}>
                      {hasAlerts && (
                        <span style={{ fontSize: '11px', background: '#fef3c7', color: '#92400e',
                          padding: '3px 8px', borderRadius: '20px', fontWeight: 600 }}>
                          ⚠️ Facture(s) en attente
                        </span>
                      )}
                      {hasPendingReminder && !hasAlerts && (
                        <span style={{ fontSize: '11px', background: '#eff6ff', color: '#1e40af',
                          padding: '3px 8px', borderRadius: '20px', fontWeight: 600 }}>
                          🔔 Relance prévue
                        </span>
                      )}
                      {c.produits_interet && (
                        <span style={{ fontSize: '11px', background: '#eff6ff', color: '#1e40af',
                          padding: '3px 8px', borderRadius: '20px', whiteSpace: 'nowrap' }}>
                          {c.produits_interet.nom}
                        </span>
                      )}
                      <span style={{ fontSize: '11px', background: statut.bg, color: statut.color,
                        padding: '3px 8px', borderRadius: '20px', fontWeight: 600 }}>
                        {statut.label}
                      </span>
                      {isManager && c.profiles && (
                        <span style={{ fontSize: '11px', color: '#9ca3af' }}>{c.profiles.nom?.split(' ')[0]}</span>
                      )}
                      <ChevronDown size={14} color="#9ca3af"
                        style={{ transform: isExpanded ? 'rotate(180deg)' : 'none', transition: '0.2s' }} />
                    </div>
                  </div>

                  {/* Détails expandables */}
                  <AnimatePresence>
                    {isExpanded && (
                      <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2 }}>

                        {/* Infos de base */}
                        <div style={{ padding: '0 16px 12px', borderTop: '1px solid #f3f4f6' }}>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                            gap: '8px', marginTop: '12px' }}>
                            {c.telephone && <div style={{ fontSize: '12px', color: '#6b7280' }}>📞 {c.telephone}</div>}
                            {c.pays && <div style={{ fontSize: '12px', color: '#6b7280' }}>🌍 {c.pays}</div>}
                            <div style={{ fontSize: '12px', color: '#6b7280' }}>
                              Source : {c.source === 'devis' ? '📄 Devis auto' : '✋ Saisie manuelle'}
                            </div>
                            <div style={{ fontSize: '12px', color: '#6b7280' }}>
                              Créé le : {c.created_at ? new Date(c.created_at).toLocaleDateString('fr-FR') : '—'}
                            </div>
                          </div>
                          {c.notes && (
                            <div style={{ marginTop: '10px', padding: '10px', background: '#f9fafb',
                              borderRadius: '8px', fontSize: '12px', color: '#374151' }}>
                              📝 {c.notes}
                            </div>
                          )}
                          <div style={{ display: 'flex', gap: '8px', marginTop: '12px', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div style={{ display: 'flex', gap: '8px' }}>
                            <button onClick={(e) => { e.stopPropagation(); openEdit(c); }}
                              style={{ padding: '6px 14px', background: '#eff6ff', color: '#1e40af',
                                border: '1px solid #bfdbfe', borderRadius: '6px', fontSize: '12px',
                                fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <Edit2 size={12} /> Modifier
                            </button>
                            <button onClick={(e) => { e.stopPropagation(); nouveauDevisClient(c); }}
                              style={{ padding: '6px 14px', background: '#ecfdf5', color: '#065f46',
                                border: '1px solid #6ee7b7', borderRadius: '6px', fontSize: '12px',
                                fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>
                              ✏️ Nouveau devis
                            </button>
                            </div>
                            {isManager && (
                              <button onClick={(e) => { e.stopPropagation(); handleDelete(c.id); }}
                                style={{ padding: '6px 14px', background: '#fef2f2', color: '#ef4444',
                                  border: '1px solid #fecaca', borderRadius: '6px', fontSize: '12px',
                                  fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <Trash2 size={12} /> Supprimer
                              </button>
                            )}
                          </div>
                        </div>

                        {/* Historique 360° */}
                        <HistoriqueClient client={c} />
                        {/* Suivi installation — manager uniquement */}
                        {isManager && <div style={{ borderTop: '1px solid #e5e7eb', background: '#f9fafb', padding: '12px 16px' }}>
                          <div style={{ fontSize: '12px', fontWeight: 700, color: '#0369a1', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            Suivi installation
                          </div>
                          <SuiviInstallations clientId={c.id} compact={true} />
                        </div>}

                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })}
          </div>

          {/* Bouton voir tous */}
          {filtered.length > 5 && (
            <button onClick={() => setShowAll(v => !v)}
              onMouseEnter={e => { e.currentTarget.style.background='#e0e7ff'; e.currentTarget.style.borderColor='#a5b4fc'; }}
              onMouseLeave={e => { e.currentTarget.style.background='#f0f4f8'; e.currentTarget.style.borderColor='#e5e7eb'; }}
              style={{ width: '100%', marginTop: '12px', padding: '10px',
                background: '#f0f4f8', border: '1px solid #e5e7eb', borderRadius: '8px',
                cursor: 'pointer', fontSize: '13px', color: '#1e40af', fontWeight: 600, transition: 'all 0.15s' }}>
              {showAll ? '▲ Réduire la liste' : `▼ Voir tous les ${filtered.length} clients`}
            </button>
          )}
        </>
      )}

      {/* ── Modal formulaire client ── */}
      <AnimatePresence>
        {showForm && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000,
              display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
            <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              style={{ background: 'white', borderRadius: '16px', width: '100%', maxWidth: '560px',
                maxHeight: '90vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
              <div style={{ padding: '20px 24px', borderBottom: '1px solid #e5e7eb',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ fontWeight: 700, fontSize: '16px', color: '#111827' }}>
                  {editClient ? 'Modifier le client' : 'Nouveau client'}
                </div>
                <button onClick={() => { setShowForm(false); setEditClient(null); setForm(emptyForm); }}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280' }}>
                  <X size={20} />
                </button>
              </div>
              <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <div>
                  <label style={labelStyle}>Nom / Raison sociale *</label>
                  <input value={form.nom} onChange={e => setForm(f => ({ ...f, nom: e.target.value }))}
                    placeholder="Ex: Mamadou Diallo" style={inputStyle} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div>
                    <label style={labelStyle}>Email</label>
                    <input value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                      placeholder="email@exemple.com" style={inputStyle} type="email" />
                  </div>
                  <div>
                    <label style={labelStyle}>Mobile</label>
                    <input value={form.mobile} onChange={e => setForm(f => ({ ...f, mobile: e.target.value }))}
                      placeholder="(+221) 77 xxx xx xx" style={inputStyle} />
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div>
                    <label style={labelStyle}>Téléphone fixe</label>
                    <input value={form.telephone} onChange={e => setForm(f => ({ ...f, telephone: e.target.value }))}
                      placeholder="(+221) 33 xxx xx xx" style={inputStyle} />
                  </div>
                  <div>
                    <label style={labelStyle}>Ville</label>
                    <input value={form.ville} onChange={e => setForm(f => ({ ...f, ville: e.target.value }))}
                      placeholder="Dakar" style={inputStyle} />
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div>
                    <label style={labelStyle}>Pays</label>
                    <input value={form.pays} onChange={e => setForm(f => ({ ...f, pays: e.target.value }))}
                      placeholder="Sénégal" style={inputStyle} />
                  </div>
                  <div>
                    <label style={labelStyle}>Statut</label>
                    <select value={form.statut} onChange={e => setForm(f => ({ ...f, statut: e.target.value }))}
                      style={{ ...inputStyle, cursor: 'pointer' }}>
                      {Object.entries(STATUTS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label style={labelStyle}>Produit d'intérêt</label>
                  <select value={form.produit_interet_id}
                    onChange={e => setForm(f => ({ ...f, produit_interet_id: e.target.value }))}
                    style={{ ...inputStyle, cursor: 'pointer' }}>
                    <option value="">— Sélectionner —</option>
                    {produits.map(p => <option key={p.id} value={p.id}>{p.nom}</option>)}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Notes</label>
                  <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                    placeholder="Informations complémentaires..."
                    rows={3} style={{ ...inputStyle, resize: 'vertical' }} />
                </div>
                {isManager && !editClient && (
                  <div>
                    <label style={labelStyle}>Assigner à</label>
                    <select value={form.commerciale_id || ''}
                      onChange={e => setForm(f => ({ ...f, commerciale_id: e.target.value }))}
                      style={{ ...inputStyle, cursor: 'pointer' }}>
                      <option value="">— Moi-même —</option>
                      {profiles.map(p => <option key={p.id} value={p.id}>{p.nom}</option>)}
                    </select>
                  </div>
                )}
              </div>
              <div style={{ padding: '16px 24px', borderTop: '1px solid #e5e7eb',
                display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <button onClick={() => { setShowForm(false); setEditClient(null); setForm(emptyForm); }}
                  style={{ padding: '8px 18px', background: '#f3f4f6', border: '1px solid #e5e7eb',
                    borderRadius: '8px', fontSize: '13px', cursor: 'pointer' }}>
                  Annuler
                </button>
                <button onClick={handleSave} disabled={saving || !form.nom.trim()}
                  style={{ padding: '8px 20px', background: saving || !form.nom.trim() ? '#93c5fd' : '#1e40af',
                    color: 'white', border: 'none', borderRadius: '8px', fontSize: '13px',
                    fontWeight: 600, cursor: saving || !form.nom.trim() ? 'not-allowed' : 'pointer' }}>
                  {saving ? 'Enregistrement...' : editClient ? 'Mettre à jour' : 'Enregistrer'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Modal ajout produit ── */}
      <AnimatePresence>
        {showProduitForm && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000,
              display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
            <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }}
              style={{ background: 'white', borderRadius: '16px', width: '100%', maxWidth: '400px',
                padding: '24px', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
              <div style={{ fontWeight: 700, fontSize: '16px', marginBottom: '16px' }}>
                Ajouter un produit d'intérêt
              </div>
              <input value={newProduit} onChange={e => setNewProduit(e.target.value)}
                placeholder="Ex: Kit Pompe Solaire 3m³/h"
                style={inputStyle} autoFocus
                onKeyDown={e => e.key === 'Enter' && handleAddProduit()} />
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '16px' }}>
                <button onClick={() => { setShowProduitForm(false); setNewProduit(''); }}
                  style={{ padding: '8px 18px', background: '#f3f4f6', border: '1px solid #e5e7eb',
                    borderRadius: '8px', fontSize: '13px', cursor: 'pointer' }}>
                  Annuler
                </button>
                <button onClick={handleAddProduit} disabled={savingProduit || !newProduit.trim()}
                  style={{ padding: '8px 20px', background: '#1e40af', color: 'white', border: 'none',
                    borderRadius: '8px', fontSize: '13px', fontWeight: 600,
                    cursor: savingProduit || !newProduit.trim() ? 'not-allowed' : 'pointer' }}>
                  {savingProduit ? 'Ajout...' : 'Ajouter'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}